import { ActionIcon, Group, Modal, Stack, Text, Tooltip } from '@mantine/core';
import { formatHumanName, resolveId } from '@medplum/core';
import { DocumentReference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconDownload, IconEye, IconX } from '@tabler/icons-react';
import { ReactElement, useState } from 'react';
import { DocumentPreview } from '@/components/DocumentPreview';
import { AttachmentRow } from '@/components/Timeline/AttachmentRow';
import { ActorAvatar } from '@/components/Timeline/TimelineItems/ActorAvatar';
import { DateTimeTooltip } from '@/components/Timeline/TimelineItems/DateTimeTooltip';
import { TimelineCard } from '@/components/Timeline/TimelineItems/TimelineCard';
import { TimelineBenefitEvent } from '@/components/Timeline/types';
import { TeammateAvatar } from '@/components/TeamAvatar';
import { useProviderDirectory } from '@/hooks/useProviderDirectory';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { downloadAttachment } from '@/services/attachments';

/** A recorded file, viewed in the Documents tab's preview or downloaded. */
function CardFile({ document }: { document: DocumentReference }): ReactElement {
  const medplum = useMedplum();
  const [previewing, setPreviewing] = useState(false);
  const attachment = document.content?.[0]?.attachment;
  const name = attachment?.title ?? 'Untitled';

  return (
    <>
      <AttachmentRow
        variant="card"
        name={name}
        size={attachment?.size}
        actions={
          attachment?.url && (
            <>
              <Tooltip label="Preview document">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label={`Preview ${name}`}
                  onClick={() => setPreviewing(true)}
                >
                  <IconEye size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Download document">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label={`Download ${name}`}
                  onClick={() => void downloadAttachment(medplum, attachment)}
                >
                  <IconDownload size={16} />
                </ActionIcon>
              </Tooltip>
            </>
          )
        }
      />
      <Modal
        opened={previewing}
        onClose={() => setPreviewing(false)}
        title={name}
        centered
        size="95vw"
        closeButtonProps={{ icon: <IconX size={20} /> }}
      >
        <DocumentPreview content={document.content?.[0]} height="35vw" width="100%" />
      </Modal>
    </>
  );
}

/**
 * One coverage step (BAR-2593, BAR-3078), employer or insurance: the title, then who recorded
 * it and when, and a body of the step's files and note only when there are any. Append-only:
 * a correction is another card.
 */
export function BenefitEventCard({ item }: { item: TimelineBenefitEvent }): ReactElement {
  // meta.author.display is frozen at invite time; the roster carries the current name.
  const { practitioners } = useProviderDirectory();
  const { documentsContext } = useTimelineContext();
  const recordedById = item.recordedBy ? resolveId(item.recordedBy) : undefined;
  const recorder = recordedById ? practitioners.find((practitioner) => practitioner.id === recordedById) : undefined;
  const recordedByName = (recorder?.name?.[0] && formatHumanName(recorder.name[0])) || item.recordedByName;

  const files = item.documents
    .map((reference) => documentsContext?.documents.find((document) => document.id === resolveId(reference)))
    .filter((document): document is DocumentReference => !!document);
  const hasBody = files.length > 0 || !!item.note;

  return (
    <TimelineCard
      header={
        <Group justify="space-between" align="center" wrap="nowrap">
          <Text size="md" fw={600}>
            {item.title}
          </Text>
          <Group gap="md" align="center" wrap="nowrap">
            {item.recordedBy ? (
              <TeammateAvatar
                practitioner={item.recordedBy}
                label={recordedByName ? `Recorded by ${recordedByName}` : undefined}
              />
            ) : (
              !item.action && (
                <ActorAvatar variant="system" label="Moved automatically when the last prep item was done" />
              )
            )}
            <DateTimeTooltip date={item.recordedAt ?? item.timestamp} />
          </Group>
        </Group>
      }
    >
      {hasBody ? (
        <Stack gap={12}>
          {files.length > 0 && (
            <Stack gap={6}>
              {files.map((document) => (
                <CardFile key={document.id} document={document} />
              ))}
            </Stack>
          )}
          {item.note && (
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {item.note}
            </Text>
          )}
        </Stack>
      ) : null}
    </TimelineCard>
  );
}
